import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useSyncExternalStore,
  useReducer,
  type ReactNode,
} from "react";
import { v4 as uuidv4 } from "uuid";
import type { Theme } from "@/config/themes";
import "./OsAppRegistry";
import { getAppDefinition } from "./appRegistryAccess";
import { getOsDockSafeAreaBottomPx } from "./osDockSafeArea";
import { useWorkspaceConfig } from "@/hooks/useWorkspaceConfig";
import { useAuth } from "@/context/AuthContext";
import type { WorkspaceConfigWindow } from "@/services/api/workspace-config/types";
import {
  readOpenWindowsMaximized,
  writeCachedOsSettingsFromServerResponse,
} from "@/utils/osUserSettingsStorage";
import { getOsSettings } from "@/services/api/os-settings";
import {
  readSessionWindows,
  writeSessionWindows,
  clearSessionWindows,
} from "@/utils/osSessionWindows";
import type { OpenWindowConfig } from "./useWindow";
import { osInteractionRef } from "./osInteractionRef";
import { computeCascadeWindowPosition, resolveOpenWindowSize, resolveRestoreFrame } from "./clampWindowGeometry";
import { preloadOsApp } from "./osAppPrefetch";
import { logAniOsWorkspaceBootstrap } from "@/utils/aniosDiagnostics";
import {
  DEFAULT_NEXT_Z_INDEX,
  dedupeAceTaskReviewWindows,
  topVisibleWindowInWorkspace,
  visibleWindowsInWorkspace,
  type OsWindowReducerState,
} from "./osWindowReducer";
import { createOsWindowStore, type OsWindowStore } from "./osWindowStore";

/**
 * Minimal window info for context menus (avoids exposing full window state)
 */
export interface WindowMenuInfo {
  id: string;
  title: string;
  appId: string;
  workspaceId: string;
}

/**
 * Derived state for components that only need to know which apps are open.
 * Updates when windows are opened/closed and when focus order (zIndex) changes
 * in the active workspace (for dock “active app” indicators).
 */
export interface OsOpenAppsContextType {
  /** Set of app IDs that have at least one open window */
  openAppIds: Set<string>;
  /** Map of appId to array of window IDs for that app */
  windowsByAppId: Map<string, string[]>;
  /** Quick lookup: is this app open? */
  isAppOpen: (appId: string) => boolean;
  /** Get window IDs for an app */
  getWindowIdsForApp: (appId: string) => string[];
  /** Active workspace ID */
  activeWorkspaceId: string;
  /** Get minimal window info for context menus */
  getWindowsForContextMenu: (appId: string) => WindowMenuInfo[];
  /** Check if app is open in a specific workspace */
  isAppOpenInWorkspace: (appId: string, workspaceId: string) => boolean;
  /**
   * appId of the frontmost non-minimized window in the active workspace, or null if none.
   */
  focusedAppIdInActiveWorkspace: string | null;
}

/**
 * Derived state for OsDesktop - only changes when window list changes,
 * NOT on position/size updates. This prevents re-rendering the entire
 * desktop on every drag.
 */
export interface OsWindowListContextType {
  /** Map of workspaceId to array of window IDs */
  windowIdsByWorkspace: Map<string, string[]>;
  /** Get a specific window by ID */
  getWindow: (id: string) => OsWindow | undefined;
  /** Latest window list from ref — does not subscribe to geometry-only updates */
  getWindowsSnapshot: () => readonly OsWindow[];
  /** All workspaces */
  workspaces: OsWorkspace[];
  /** Active workspace ID */
  activeWorkspaceId: string;
}

// ============================================================================
// Types
// ============================================================================

export interface OsWindow {
  id: string;
  appId: string;
  title: string;
  /** App-specific params (e.g. { id: workerId }). Rendered dynamically via APP_REGISTRY. */
  params: Record<string, string>;
  workspaceId: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  minSize?: { width: number; height: number };
  zIndex: number;
  isMinimized: boolean;
  isMaximized: boolean;
  /** Set by Ani sidebar while a frontend tool runs against this window */
  isAniWorking?: boolean;
  /** Original position before sidebar adjustment (used for restoration) */
  preAdjustmentPosition?: { x: number; y: number };
  /** The adjusted position we set (used to detect manual moves) */
  adjustedPosition?: { x: number; y: number };
  /** Whether this window is in the active workspace (for suspension) */
  isActive?: boolean;
  /** Bumped when focus deep-link events repeat on the same row (not in params / URL). */
  focusNonce?: number;
}

export interface OsWorkspace {
  id: string;
  name: string;
  themeSeed?: string;
}

/**
 * Context for window/workspace state - updates frequently during drag/resize.
 * Subscribe only when you need to read window positions, sizes, or workspace list.
 */
export interface OsWindowsContextType {
  windows: OsWindow[];
  workspaces: OsWorkspace[];
  activeWorkspaceId: string;
  /**
   * Topmost non-minimized window in the active workspace (highest zIndex).
   * Used by OsAniSidebar and agent context.
   */
  getFocusedWindow: () => OsWindow | undefined;
  /** appId of focused window, or `home` when none */
  getFocusedAppId: () => string;
}

/**
 * Context for actions - stable references that never change.
 * Subscribe when you only need to call actions like openWindow, closeWindow, etc.
 */
export interface OsActionsContextType {
  openWindow: (config: OpenWindowConfig) => void;
  closeWindow: (id: string) => boolean;
  /** Close every window, bypassing close guards — used for demo reset. */
  closeAllWindows: () => void;
  /** Run close guards without closing. Returns false if any guard vetoes. */
  requestCloseWindow: (id: string) => boolean;
  focusWindow: (id: string) => void;
  updateWindow: (id: string, patch: Partial<OsWindow>) => void;
  adjustWindowsForDesktopResize: (newDesktopWidth: number, oldDesktopWidth: number) => void;
  /** Clamp non-maximized windows so size and position stay inside the desktop rect. */
  clampWindowsToDesktopBounds: (
    desktopWidth: number,
    windowedDesktopHeight: number,
    maximizedDesktopHeight?: number,
  ) => void;
  addWorkspace: () => void;
  removeWorkspace: (id: string) => void;
  switchWorkspace: (id: string) => void;
  updateWorkspaceName: (id: string, name: string) => void;
  updateWorkspaceTheme: (id: string, themeSeed: string) => void;
  getActiveWorkspace: () => OsWorkspace | undefined;
  /** Return false from a guard to block close (e.g. unsaved changes). */
  registerWindowCloseGuard: (
    windowId: string,
    guard: () => boolean,
  ) => () => void;
  /** Mark a window as dragging/resizing (updates ``osInteractionRef`` synchronously). */
  setWindowInteracting: (windowId: string, isInteracting: boolean) => void;
}

/**
 * Context for settings - updates rarely.
 * Subscribe when you need loading state, dock style, or theme management.
 */
export interface OsSettingsContextType {
  isLoading: boolean;
  dockIconStyle: "gradient" | "colored";
  setDockIconStyle: (style: "gradient" | "colored") => void;
  setAvailableThemes: (themes: Theme[]) => void;
  /** True once persisted workspace config has been applied to OS state */
  isWorkspaceReady: boolean;
}

/**
 * Context for per-window subscriptions.
 * This allows OsWindowById to subscribe to only its own window's changes
 * without being affected by other windows updating.
 */
export interface OsWindowVersionContextType {
  /** Subscribe to changes for a specific window. Returns unsubscribe function. */
  subscribeToWindow: (windowId: string, callback: () => void) => () => void;
  /** Get window data by ID (reads from ref, doesn't cause re-renders) */
  getWindow: (id: string) => OsWindow | undefined;
}

/**
 * Combined context type for backward compatibility.
 * @deprecated Use specific context hooks (useOsWindows, useOsActions, useOsSettings) for better performance.
 */
export type OsContextType = OsWindowsContextType & OsActionsContextType & OsSettingsContextType;

/** Focus snapshot for the active workspace — updates on z-order / params, not drag position. */
export interface OsFocusContextType {
  focusedWindowId: string | null;
  /** ``id:params`` key for URL sync and shallow equality checks */
  focusedWindowKey: string | null;
  getFocusedWindow: () => OsWindow | undefined;
  /** True when a non-minimized window in the active workspace is maximized. */
  hasMaximizedWindowInActiveWorkspace: boolean;
}

/**
 * Context for managing dock tooltips.
 * Allows components to set custom tooltips for dock app icons.
 */
export interface OsDockTooltipsContextType {
  /** Get the tooltip for an app (returns custom tooltip or null if default should be used) */
  getTooltip: (appId: string) => string | null;
  /** Set a custom tooltip for an app (pass null to clear) */
  setTooltip: (appId: string, tooltip: string | null) => void;
  /** Clear all custom tooltips */
  clearAll: () => void;
}

// ============================================================================
// Contexts
// ============================================================================

const OsWindowsContext = createContext<OsWindowsContextType | undefined>(undefined);
const OsActionsContext = createContext<OsActionsContextType | undefined>(undefined);
const OsSettingsContext = createContext<OsSettingsContextType | undefined>(undefined);
const OsOpenAppsContext = createContext<OsOpenAppsContextType | undefined>(undefined);
const OsWindowListContext = createContext<OsWindowListContextType | undefined>(undefined);
const OsWindowVersionContext = createContext<OsWindowVersionContextType | undefined>(undefined);
const OsFocusContext = createContext<OsFocusContextType | undefined>(undefined);
const OsDockTooltipsContext = createContext<OsDockTooltipsContextType | undefined>(undefined);

// Keep legacy context for useIsOsMode check
const OsContext = createContext<boolean>(false);
const OsWindowStoreContext = createContext<OsWindowStore | null>(null);

function useStoreSelector<T>(
  store: OsWindowStore,
  selector: (state: OsWindowReducerState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T {
  const selectionRef = useRef<T>(selector(store.getState()));

  const getSnapshot = useCallback(() => {
    const next = selector(store.getState());
    if (isEqual(selectionRef.current, next)) {
      return selectionRef.current;
    }
    selectionRef.current = next;
    return next;
  }, [store, selector, isEqual]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

// ============================================================================
// Hooks - Split Contexts (Recommended)
// ============================================================================

/**
 * Hook to access window/workspace state. Updates on every window position/size change.
 * Use this only when you need to read the windows array or workspace list.
 */
export const useOsWindows = (): OsWindowsContextType => {
  const context = useContext(OsWindowsContext);
  if (!context) {
    throw new Error("useOsWindows must be used within an OsProvider");
  }
  return context;
};

/**
 * Hook to access OS actions. Stable references - never triggers re-renders.
 * Use this when you only need to call openWindow, closeWindow, etc.
 */
export const useOsActions = (): OsActionsContextType => {
  const context = useContext(OsActionsContext);
  if (!context) {
    throw new Error("useOsActions must be used within an OsProvider");
  }
  return context;
};

/**
 * Hook to access OS settings. Updates rarely (loading state, dock style).
 */
export const useOsSettings = (): OsSettingsContextType => {
  const context = useContext(OsSettingsContext);
  if (!context) {
    throw new Error("useOsSettings must be used within an OsProvider");
  }
  return context;
};

/**
 * Hook to access which apps are open and which app is focused in the active workspace.
 * Updates on open/close and on focus order (zIndex) in the active workspace — not on drag/resize only.
 */
export const useOsOpenApps = (): OsOpenAppsContextType => {
  const context = useContext(OsOpenAppsContext);
  if (!context) {
    throw new Error("useOsOpenApps must be used within an OsProvider");
  }
  return context;
};

/**
 * Hook to access window list by workspace. Only updates when windows are opened/closed,
 * NOT on position/size changes. Use this for OsDesktop to avoid re-rendering on every drag.
 */
export const useOsWindowList = (): OsWindowListContextType => {
  const context = useContext(OsWindowListContext);
  if (!context) {
    throw new Error("useOsWindowList must be used within an OsProvider");
  }
  return context;
};

/**
 * Hook to access window version context for subscribing to individual window changes.
 */
export const useOsWindowVersion = (): OsWindowVersionContextType => {
  const context = useContext(OsWindowVersionContext);
  if (!context) {
    throw new Error("useOsWindowVersion must be used within an OsProvider");
  }
  return context;
};

/** Focus in the active workspace — re-renders on z-order / minimize / param changes only. */
export const useOsFocus = (): OsFocusContextType => {
  const context = useContext(OsFocusContext);
  if (!context) {
    throw new Error("useOsFocus must be used within an OsProvider");
  }
  return context;
};

/**
 * Hook to access dock tooltip management.
 * Use this to set custom tooltips for dock app icons.
 *
 * @example
 * ```tsx
 * const { setTooltip } = useOsDockTooltips();
 *
 * // Set a custom tooltip for the pods app
 * useEffect(() => {
 *   setTooltip("pods", "Daily IHF Reconciliations");
 *   return () => setTooltip("pods", null); // Clear on unmount
 * }, [setTooltip]);
 * ```
 */
export const useOsDockTooltips = (): OsDockTooltipsContextType => {
  const context = useContext(OsDockTooltipsContext);
  if (!context) {
    throw new Error("useOsDockTooltips must be used within an OsProvider");
  }
  return context;
};

/**
 * Optional hook to access dock tooltip management (returns null outside OsProvider).
 */
export const useOsDockTooltipsOptional = (): OsDockTooltipsContextType | null => {
  return useContext(OsDockTooltipsContext) ?? null;
};

/**
 * Hook to get a single window's data. Only re-renders when THIS window changes.
 * Use this inside OsWindow to subscribe to just its own data.
 * @deprecated Use useWindowById for better performance
 */
export const useOsWindowData = (windowId: string): OsWindow | undefined => {
  const { windows } = useOsWindows();
  return useMemo(() => windows.find(w => w.id === windowId), [windows, windowId]);
};

/**
 * Optional variant of useOsWindowVersion. Returns null if outside OsProvider.
 */
export const useOsWindowVersionOptional =
  (): OsWindowVersionContextType | null => {
    return useContext(OsWindowVersionContext) ?? null;
  };

/**
 * Hook to get a single window's data with efficient subscriptions.
 * Only re-renders when THIS specific window changes, not when other windows change.
 * Uses useReducer + useEffect subscription pattern for efficient updates.
 *
 * Returns `undefined` when there is no `OsProvider` ancestor (e.g. React Fast Refresh
 * between context module reload and provider re-mount). Callers should treat that like
 * missing window data (`OsWindowById` renders null).
 */
export const useWindowById = (
  windowId: string | undefined | null,
): OsWindow | undefined => {
  const store = useContext(OsWindowStoreContext);
  const selectionRef = useRef<OsWindow | undefined>(undefined);

  const getSnapshot = useCallback(() => {
    if (!store || !windowId) {
      return undefined;
    }
    const next = store.getState().windows.find((w) => w.id === windowId);
    if (selectionRef.current === next) {
      return selectionRef.current;
    }
    selectionRef.current = next;
    return next;
  }, [store, windowId]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!store || !windowId) {
        return () => {};
      }
      return store.subscribe(onStoreChange);
    },
    [store, windowId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

// ============================================================================
// Hooks - Optional Variants
// ============================================================================

/**
 * Optional variant of useOsWindows. Returns null if outside OsProvider.
 */
export const useOsWindowsOptional = (): OsWindowsContextType | null => {
  return useContext(OsWindowsContext) ?? null;
};

/**
 * Optional variant of useOsActions. Returns null if outside OsProvider.
 */
export const useOsActionsOptional = (): OsActionsContextType | null => {
  return useContext(OsActionsContext) ?? null;
};

/**
 * Optional variant of useOsSettings. Returns null if outside OsProvider.
 */
export const useOsSettingsOptional = (): OsSettingsContextType | null => {
  return useContext(OsSettingsContext) ?? null;
};

/**
 * Optional variant of useOsOpenApps. Returns null if outside OsProvider.
 */
export const useOsOpenAppsOptional = (): OsOpenAppsContextType | null => {
  return useContext(OsOpenAppsContext) ?? null;
};

/**
 * Optional variant of useOsWindowList. Returns null if outside OsProvider.
 * Use at layout boundaries so Fast Refresh can skip one frame without throwing.
 */
export const useOsWindowListOptional = (): OsWindowListContextType | null => {
  return useContext(OsWindowListContext) ?? null;
};

// ============================================================================
// Hooks - Legacy (Backward Compatibility)
// ============================================================================

/**
 * Combined hook for backward compatibility.
 * @deprecated Use useOsWindows, useOsActions, or useOsSettings for better performance.
 */
export const useOs = (): OsContextType => {
  const windows = useOsWindows();
  const actions = useOsActions();
  const settings = useOsSettings();
  return { ...windows, ...actions, ...settings };
};

/**
 * Hook to check if the component is running in OS mode (window-based layout).
 * Returns true if inside an OsProvider, false otherwise.
 */
export const useIsOsMode = (): boolean => {
  return useContext(OsContext);
};

/**
 * Hook to optionally get the OS context. Returns null if not inside an OsProvider.
 * Use this when a component can work both inside and outside of OS mode.
 * @deprecated Use useOsActionsOptional, useOsWindowsOptional, or useOsSettingsOptional for better performance.
 */
export const useOsOptional = (): OsContextType | null => {
  const windows = useContext(OsWindowsContext);
  const actions = useContext(OsActionsContext);
  const settings = useContext(OsSettingsContext);

  if (!windows || !actions || !settings) {
    return null;
  }

  return { ...windows, ...actions, ...settings };
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Round pixel / layout numbers for API persistence.
 * The backend expects integers; DOM layout math often produces floats (e.g. 110.00000762939453).
 */
function roundPx(n: number): number {
  return Math.round(n);
}

/** Desktop rect a restored window should fit, above the dock. */
function readRestoreDesktopBounds(): { width: number; height: number } | null {
  if (typeof document === "undefined") return null;
  const el = document.getElementById("main-content");
  if (!el) return null;
  const rootFs = parseFloat(getComputedStyle(document.documentElement).fontSize);
  const dockBottomPx = getOsDockSafeAreaBottomPx(
    Number.isFinite(rootFs) ? rootFs : 16,
    { reserveDockSpace: true },
  );
  return {
    width: el.clientWidth,
    height: Math.max(0, el.clientHeight - dockBottomPx),
  };
}

/**
 * Convert OsWindow[] to WorkspaceConfigWindow[] for API persistence.
 * Strips client-only fields like minSize, preAdjustmentPosition, adjustedPosition, isActive.
 */
function toApiWindows(windows: OsWindow[]): WorkspaceConfigWindow[] {
  return windows.map((w) => ({
    id: w.id,
    appId: w.appId,
    title: w.title,
    params: w.params,
    workspaceId: w.workspaceId,
    position: { x: roundPx(w.position.x), y: roundPx(w.position.y) },
    size: {
      width: roundPx(w.size.width),
      height: roundPx(w.size.height),
    },
    zIndex: roundPx(w.zIndex),
    isMinimized: w.isMinimized,
    isMaximized: w.isMaximized,
  }));
}

/**
 * Convert WorkspaceConfigWindow[] to OsWindow[] from API.
 * Adds client-only default values.
 */
function fromApiWindows(windows: WorkspaceConfigWindow[]): OsWindow[] {
  return windows.map((w) => ({
    id: w.id,
    appId: w.appId,
    title: w.title,
    params: w.params as Record<string, string>,
    workspaceId: w.workspaceId,
    position: w.position,
    size: w.size,
    zIndex: w.zIndex,
    isMinimized: w.isMinimized,
    isMaximized: w.isMaximized,
  }));
}

/** Attach windows whose workspaceId no longer exists (stale session restore). */
export function reconcileWindowsToWorkspaces(
  windows: OsWindow[],
  workspaces: OsWorkspace[],
  activeWorkspaceId: string,
): OsWindow[] {
  if (windows.length === 0 || workspaces.length === 0) {
    return windows;
  }

  const validWorkspaceIds = new Set(workspaces.map((ws) => ws.id));
  const fallbackWorkspaceId = validWorkspaceIds.has(activeWorkspaceId)
    ? activeWorkspaceId
    : workspaces[0].id;

  let changed = false;
  const nextWindows = windows.map((window) => {
    if (validWorkspaceIds.has(window.workspaceId)) {
      return window;
    }
    changed = true;
    return { ...window, workspaceId: fallbackWorkspaceId };
  });

  return changed ? nextWindows : windows;
}

// ============================================================================
// Provider
// ============================================================================

/** Default configuration for new users */
const DEFAULT_WORKSPACES: OsWorkspace[] = [{ id: "ws-1", name: "Workspace 1", themeSeed: "cosmos" }];
const DEFAULT_ACTIVE_WORKSPACE_ID = "ws-1";
const DEFAULT_DOCK_ICON_STYLE: "gradient" | "colored" = "colored";

export const OsProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<OsWindowStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createOsWindowStore({
      windows: [],
      workspaces: DEFAULT_WORKSPACES,
      activeWorkspaceId: DEFAULT_ACTIVE_WORKSPACE_ID,
      nextZIndex: DEFAULT_NEXT_Z_INDEX,
    });
  }
  const store = storeRef.current;

  return (
    <OsWindowStoreContext.Provider value={store}>
      <OsProviderRuntime store={store}>{children}</OsProviderRuntime>
    </OsWindowStoreContext.Provider>
  );
};

function OsProviderRuntime({
  store,
  children,
}: {
  store: OsWindowStore;
  children: ReactNode;
}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const { config, isLoading: isWorkspaceConfigLoading, saveConfig } = useWorkspaceConfig();
  const configReady = !isWorkspaceConfigLoading;
  const [isInitialized, setIsInitialized] = useState(false);
  const [dockIconStyle, setDockIconStyle] = useState<"gradient" | "colored">(DEFAULT_DOCK_ICON_STYLE);
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);
  const availableThemesRef = useRef(availableThemes);
  availableThemesRef.current = availableThemes;
  const closeGuardsRef = useRef(new Map<string, Set<() => boolean>>());
  const interactingWindowsRef = useRef<Set<string>>(new Set());
  const openWindowsMaximizedRef = useRef(false);
  const osSettingsReconciledRef = useRef(false);
  const bootstrapWindowIdsRef = useRef<Set<string>>(new Set());
  const isInitializedRef = useRef(isInitialized);
  isInitializedRef.current = isInitialized;

  const applyBootstrapMaximize = useCallback(() => {
    if (!isInitializedRef.current || !userId) return;
    if (!openWindowsMaximizedRef.current) {
      bootstrapWindowIdsRef.current.clear();
      return;
    }

    const bootstrapIds = bootstrapWindowIdsRef.current;
    if (bootstrapIds.size === 0) return;

    for (const windowId of bootstrapIds) {
      const w = store.getState().windows.find((win) => win.id === windowId);
      if (!w || w.isMinimized || w.isMaximized) continue;
      const appDef = getAppDefinition(w.appId);
      if (appDef?.openWindowed) continue;
      store.dispatch({
        type: "update",
        windowId: w.id,
        patch: { isMaximized: true },
      });
    }
    bootstrapIds.clear();
  }, [store, userId]);

  useEffect(() => {
    if (!userId) return;

    openWindowsMaximizedRef.current = readOpenWindowsMaximized(userId);
    osSettingsReconciledRef.current = false;
    bootstrapWindowIdsRef.current.clear();

    let cancelled = false;
    getOsSettings()
      .then((server) => {
        if (cancelled) return;
        osSettingsReconciledRef.current = true;

        if (server) {
          const openMaximizedPref = server.openWindowsMaximized === true;
          openWindowsMaximizedRef.current = openMaximizedPref;
          writeCachedOsSettingsFromServerResponse(userId, server);
        }

        applyBootstrapMaximize();
      })
      .catch((err) => {
        console.error("[OsContext] failed to load os-settings:", err);
        if (!cancelled) {
          osSettingsReconciledRef.current = true;
          bootstrapWindowIdsRef.current.clear();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId, applyBootstrapMaximize]);

  useEffect(() => {
    if (isInitialized && userId && osSettingsReconciledRef.current) {
      applyBootstrapMaximize();
    }
  }, [isInitialized, userId, applyBootstrapMaximize]);

  const windows = useStoreSelector(store, (s) => s.windows);
  const workspaces = useStoreSelector(store, (s) => s.workspaces);
  const activeWorkspaceId = useStoreSelector(store, (s) => s.activeWorkspaceId);
  const nextZIndex = useStoreSelector(store, (s) => s.nextZIndex);

  const setWindowInteracting = useCallback((windowId: string, isInteracting: boolean) => {
    if (isInteracting) {
      interactingWindowsRef.current.add(windowId);
    } else {
      interactingWindowsRef.current.delete(windowId);
    }
    osInteractionRef.current = interactingWindowsRef.current.size > 0;
  }, []);

  const hydrateStore = useCallback(
    (partial: Partial<OsWindowReducerState> & Pick<OsWindowReducerState, "workspaces" | "activeWorkspaceId">) => {
      const current = store.getState();
      store.dispatch({
        type: "hydrate",
        state: {
          windows: partial.windows ?? current.windows,
          workspaces: partial.workspaces,
          activeWorkspaceId: partial.activeWorkspaceId,
          nextZIndex: partial.nextZIndex ?? current.nextZIndex,
        },
      });
    },
    [store],
  );

  useLayoutEffect(() => {
    if (!configReady || isInitialized) return;

    if (config) {
      const incomingWorkspaces = config.workspaces as OsWorkspace[];
      const currentWorkspaces = store.getState().workspaces;
      const preserveLocalWorkspaces = currentWorkspaces.length > incomingWorkspaces.length;
      const nextWorkspaces = preserveLocalWorkspaces ? currentWorkspaces : incomingWorkspaces;
      const currentActive = store.getState().activeWorkspaceId;
      const nextActiveWorkspaceId = preserveLocalWorkspaces
        ? nextWorkspaces.some((w) => w.id === currentActive)
          ? currentActive
          : nextWorkspaces[0].id
        : nextWorkspaces.some((w) => w.id === config.activeWorkspaceId)
          ? config.activeWorkspaceId
          : nextWorkspaces[0].id;

      const sessionWindows = userId
        ? dedupeAceTaskReviewWindows(
            reconcileWindowsToWorkspaces(
              fromApiWindows(readSessionWindows(userId) ?? []),
              nextWorkspaces,
              nextActiveWorkspaceId,
            ),
          )
        : [];
      const loadedNextZIndex =
        sessionWindows.length > 0
          ? Math.max(...sessionWindows.map((w) => w.zIndex)) + 1
          : (config.nextZIndex ?? DEFAULT_NEXT_Z_INDEX);

      hydrateStore({
        workspaces: nextWorkspaces,
        activeWorkspaceId: nextActiveWorkspaceId,
        windows: sessionWindows,
        nextZIndex: loadedNextZIndex,
      });
      setDockIconStyle((config.dockIconStyle as "gradient" | "colored") ?? DEFAULT_DOCK_ICON_STYLE);
      setIsInitialized(true);
      logAniOsWorkspaceBootstrap("initialized-from-config", {
        windowCount: sessionWindows.length,
        workspaceCount: nextWorkspaces.length,
      });
    } else {
      if (userId) {
        const loadedWindows = dedupeAceTaskReviewWindows(
          reconcileWindowsToWorkspaces(
            fromApiWindows(readSessionWindows(userId) ?? []),
            store.getState().workspaces,
            store.getState().activeWorkspaceId,
          ),
        );
        if (loadedWindows.length > 0) {
          const loadedNextZIndex = Math.max(...loadedWindows.map((w) => w.zIndex)) + 1;
          hydrateStore({
            workspaces: store.getState().workspaces,
            activeWorkspaceId: store.getState().activeWorkspaceId,
            windows: loadedWindows,
            nextZIndex: loadedNextZIndex,
          });
        }
      }
      setIsInitialized(true);
      logAniOsWorkspaceBootstrap("initialized-default", {
        windowCount: store.getState().windows.length,
      });
    }
  }, [configReady, isInitialized, config, userId, hydrateStore, store]);

  useEffect(() => {
    if (!isInitialized) return;
    const current = store.getState();
    const nextWindows = reconcileWindowsToWorkspaces(
      current.windows,
      current.workspaces,
      current.activeWorkspaceId,
    );
    if (nextWindows !== current.windows) {
      store.dispatch({
        type: "hydrate",
        state: { ...current, windows: nextWindows },
      });
    }
  }, [isInitialized, workspaces, activeWorkspaceId, store]);

  const saveConfigRef = useRef(saveConfig);
  saveConfigRef.current = saveConfig;
  const persistDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isInitialized) return;

    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
    }

    persistDebounceRef.current = setTimeout(() => {
      const { windows: w, workspaces: ws, activeWorkspaceId: aw, nextZIndex: nz } =
        store.getState();
      const apiWindows = toApiWindows(w);

      if (userId) {
        writeSessionWindows(userId, apiWindows);
      }

      saveConfigRef.current({
        workspaces: ws,
        activeWorkspaceId: aw,
        windows: [],
        nextZIndex: roundPx(nz),
        dockIconStyle,
      });
    }, 300);

    return () => {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
      }
    };
  }, [windows, workspaces, activeWorkspaceId, nextZIndex, dockIconStyle, isInitialized, userId, store]);

  const flushSessionWindowsToStorage = useCallback(() => {
    if (!userId) return;
    writeSessionWindows(userId, toApiWindows(store.getState().windows));
  }, [userId, store]);

  const focusWindow = useCallback(
    (id: string) => {
      store.dispatch({ type: "focus", windowId: id });
    },
    [store],
  );

  const openWindow = useCallback(
    (config: OpenWindowConfig) => {
      const {
        appId,
        title,
        params,
        initialSize,
        maximize,
        demoteWindowId,
        stackBelowWindowId,
        focus = true,
        newWindow,
        replaceParams,
      } = config;
      const appDef = getAppDefinition(appId);
      void preloadOsApp(appId);

      const { activeWorkspaceId: workspaceId, windows: currentWindows } = store.getState();

      const desktopElement = document.getElementById("main-content");
      const desktopWidth = desktopElement?.clientWidth || window.innerWidth;
      const rootFs = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const dockBottomPx = getOsDockSafeAreaBottomPx(Number.isFinite(rootFs) ? rootFs : 16, {
        reserveDockSpace: true,
      });
      const rawHeight = desktopElement?.clientHeight || window.innerHeight;
      const windowedDesktopHeight = Math.max(0, rawHeight - dockBottomPx);

      const forceWindowed = appDef?.openWindowed === true;
      const appDefaultSize = appDef?.defaultSize;
      const defaultOpenMaximized =
        openWindowsMaximizedRef.current || readOpenWindowsMaximized(userId);
      if (defaultOpenMaximized) {
        openWindowsMaximizedRef.current = true;
      }
      const openMaximized =
        !forceWindowed &&
        (maximize !== undefined ? maximize : defaultOpenMaximized);

      const windowedInWorkspace = currentWindows.filter(
        (w) => w.workspaceId === workspaceId && !w.isMaximized && !w.isMinimized,
      );
      const cascadeBaseWindow = windowedInWorkspace.reduce<OsWindow | undefined>(
        (top, w) => (top === undefined || w.zIndex > top.zIndex ? w : top),
        undefined,
      );

      let size: { width: number; height: number };
      let position: { x: number; y: number };

      if (openMaximized) {
        size = { width: desktopWidth, height: rawHeight };
        position = { x: 0, y: 0 };
      } else {
        size = resolveOpenWindowSize({
          openWindowed: forceWindowed,
          appDefaultSize,
          initialSize,
          desktopWidth,
          windowedDesktopHeight,
        });
        position = computeCascadeWindowPosition(
          size,
          desktopWidth,
          windowedDesktopHeight,
          cascadeBaseWindow?.position,
        );
      }

      const newWindowId = uuidv4();
      store.dispatch({
        type: "open",
        payload: {
          appId,
          title,
          params,
          focus,
          demoteWindowId,
          stackBelowWindowId,
          newWindow,
          replaceParams,
          newWindowId,
          minSize: appDef?.minSize,
          workspaceId,
          position,
          size,
          isMaximized: openMaximized,
          demoteMaximizedOnFocus: appDef?.demoteMaximizedOnFocus,
        },
      });

      if (!osSettingsReconciledRef.current) {
        bootstrapWindowIdsRef.current.add(newWindowId);
      }

      queueMicrotask(() => {
        flushSessionWindowsToStorage();
      });
    },
    [store, flushSessionWindowsToStorage, userId],
  );

  const registerWindowCloseGuard = useCallback((windowId: string, guard: () => boolean) => {
    let guards = closeGuardsRef.current.get(windowId);
    if (!guards) {
      guards = new Set();
      closeGuardsRef.current.set(windowId, guards);
    }
    guards.add(guard);
    return () => {
      guards!.delete(guard);
      if (guards!.size === 0) {
        closeGuardsRef.current.delete(windowId);
      }
    };
  }, []);

  const requestCloseWindow = useCallback((id: string): boolean => {
    const guards = closeGuardsRef.current.get(id);
    if (guards) {
      for (const guard of guards) {
        if (!guard()) return false;
      }
    }
    return true;
  }, []);

  const closeAllWindows = useCallback(() => {
    closeGuardsRef.current.clear();
    store.dispatch({ type: "closeAll" });
    if (userId) {
      clearSessionWindows(userId);
    }
  }, [store, userId]);

  const closeWindow = useCallback(
    (id: string): boolean => {
      if (!requestCloseWindow(id)) return false;
      const exists = store.getState().windows.some((w) => w.id === id);
      if (!exists) return false;
      store.dispatch({ type: "close", windowId: id });
      return true;
    },
    [requestCloseWindow, store],
  );

  const updateWindow = useCallback(
    (id: string, patch: Partial<OsWindow>) => {
      let appliedPatch = patch;
      const targetWindow = store.getState().windows.find((w) => w.id === id);
      if (
        targetWindow?.isMaximized &&
        patch.isMaximized === false &&
        patch.size === undefined &&
        patch.position === undefined
      ) {
        const bounds = readRestoreDesktopBounds();
        const frame = bounds
          ? resolveRestoreFrame(targetWindow, bounds.width, bounds.height)
          : null;
        if (frame) {
          appliedPatch = { ...patch, ...frame };
        }
      }
      store.dispatch({ type: "update", windowId: id, patch: appliedPatch });
    },
    [store],
  );

  const adjustWindowsForDesktopResize = useCallback(
    (newDesktopWidth: number, oldDesktopWidth: number) => {
      store.dispatch({
        type: "adjustForResize",
        newDesktopWidth,
        oldDesktopWidth,
      });
    },
    [store],
  );

  const clampWindowsToDesktopBounds = useCallback(
    (desktopWidth: number, windowedDesktopHeight: number, maximizedDesktopHeight?: number) => {
      if (desktopWidth <= 0 || windowedDesktopHeight <= 0) return;
      if (osInteractionRef.current) return;
      store.dispatch({
        type: "clamp",
        desktopWidth,
        windowedDesktopHeight,
        maximizedDesktopHeight,
      });
    },
    [store],
  );

  const addWorkspace = useCallback(() => {
    const currentWorkspaces = store.getState().workspaces;
    const currentThemes = availableThemesRef.current;
    const newWorkspaceNumber = currentWorkspaces.length + 1;

    let themeSeed: string | undefined;
    if (currentThemes.length > 0) {
      const themeIndex = currentWorkspaces.length % currentThemes.length;
      themeSeed = currentThemes[themeIndex].seed;
    }

    store.dispatch({
      type: "addWorkspace",
      workspace: {
        id: uuidv4(),
        name: `Workspace ${newWorkspaceNumber}`,
        themeSeed,
      },
    });
  }, [store]);

  const removeWorkspace = useCallback(
    (id: string) => {
      store.dispatch({ type: "removeWorkspace", workspaceId: id });
    },
    [store],
  );

  const switchWorkspace = useCallback(
    (id: string) => {
      store.dispatch({ type: "switchWorkspace", workspaceId: id });
    },
    [store],
  );

  const updateWorkspaceName = useCallback(
    (id: string, name: string) => {
      store.dispatch({ type: "updateWorkspaceName", workspaceId: id, name });
    },
    [store],
  );

  const updateWorkspaceTheme = useCallback(
    (id: string, themeSeed: string) => {
      store.dispatch({ type: "updateWorkspaceTheme", workspaceId: id, themeSeed });
    },
    [store],
  );

  const getActiveWorkspace = useCallback(() => {
    const { workspaces: ws, activeWorkspaceId: aw } = store.getState();
    return ws.find((w) => w.id === aw);
  }, [store]);

  const windowsValue = useMemo<OsWindowsContextType>(() => {
    const getFocusedWindow = (): OsWindow | undefined =>
      topVisibleWindowInWorkspace(windows, activeWorkspaceId);
    const getFocusedAppId = (): string => getFocusedWindow()?.appId ?? "home";

    return {
      windows,
      workspaces,
      activeWorkspaceId,
      getFocusedWindow,
      getFocusedAppId,
    };
  }, [windows, workspaces, activeWorkspaceId]);

  const actionsValue = useMemo<OsActionsContextType>(
    () => ({
      openWindow,
      closeWindow,
      closeAllWindows,
      requestCloseWindow,
      focusWindow,
      updateWindow,
      adjustWindowsForDesktopResize,
      clampWindowsToDesktopBounds,
      addWorkspace,
      removeWorkspace,
      switchWorkspace,
      updateWorkspaceName,
      updateWorkspaceTheme,
      getActiveWorkspace,
      registerWindowCloseGuard,
      setWindowInteracting,
    }),
    [
      openWindow,
      closeWindow,
      closeAllWindows,
      requestCloseWindow,
      focusWindow,
      updateWindow,
      adjustWindowsForDesktopResize,
      clampWindowsToDesktopBounds,
      addWorkspace,
      removeWorkspace,
      switchWorkspace,
      updateWorkspaceName,
      updateWorkspaceTheme,
      getActiveWorkspace,
      registerWindowCloseGuard,
      setWindowInteracting,
    ],
  );

  const isLoadingState = !isInitialized;
  const settingsValue = useMemo<OsSettingsContextType>(
    () => ({
      isLoading: isLoadingState,
      isWorkspaceReady: isInitialized,
      dockIconStyle,
      setDockIconStyle,
      setAvailableThemes,
    }),
    [isLoadingState, isInitialized, dockIconStyle],
  );

  const windowListKey = windows.map((w) => `${w.id}:${w.appId}:${w.workspaceId}`).join(",");
  const windowTitleKey = windows.map((w) => `${w.id}:${w.title}`).join(",");
  const focusOrderKey = windows
    .filter((w) => w.workspaceId === activeWorkspaceId)
    .map((w) => `${w.id}:${w.zIndex}:${w.isMinimized ? 1 : 0}:${w.isMaximized ? 1 : 0}`)
    .sort()
    .join(",");

  const openAppsValue = useMemo<OsOpenAppsContextType>(() => {
    const openAppIds = new Set<string>();
    const windowsByAppId = new Map<string, string[]>();
    const windowMenuInfoByAppId = new Map<string, WindowMenuInfo[]>();

    for (const w of windows) {
      openAppIds.add(w.appId);
      const existingIds = windowsByAppId.get(w.appId) || [];
      existingIds.push(w.id);
      windowsByAppId.set(w.appId, existingIds);

      const existingMenuInfo = windowMenuInfoByAppId.get(w.appId) || [];
      existingMenuInfo.push({
        id: w.id,
        title: w.title,
        appId: w.appId,
        workspaceId: w.workspaceId,
      });
      windowMenuInfoByAppId.set(w.appId, existingMenuInfo);
    }

    const visibleInWorkspace = windows.filter(
      (w) => w.workspaceId === activeWorkspaceId && !w.isMinimized,
    );
    const focusedAppIdInActiveWorkspace =
      visibleInWorkspace.length === 0
        ? null
        : visibleInWorkspace.reduce((top, w) => (w.zIndex >= top.zIndex ? w : top)).appId;

    return {
      openAppIds,
      windowsByAppId,
      activeWorkspaceId,
      isAppOpen: (appId: string) => openAppIds.has(appId),
      getWindowIdsForApp: (appId: string) => windowsByAppId.get(appId) || [],
      getWindowsForContextMenu: (appId: string) => windowMenuInfoByAppId.get(appId) || [],
      isAppOpenInWorkspace: (appId: string, workspaceId: string) => {
        const menuInfos = windowMenuInfoByAppId.get(appId);
        return menuInfos?.some((w) => w.workspaceId === workspaceId) ?? false;
      },
      focusedAppIdInActiveWorkspace,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowListKey, focusOrderKey, windowTitleKey, activeWorkspaceId]);

  const windowListValue = useMemo<OsWindowListContextType>(() => {
    const windowIdsByWorkspace = new Map<string, string[]>();

    for (const ws of workspaces) {
      windowIdsByWorkspace.set(ws.id, []);
    }
    for (const w of windows) {
      const list =
        windowIdsByWorkspace.get(w.workspaceId) ??
        windowIdsByWorkspace.get(activeWorkspaceId);
      if (list) {
        list.push(w.id);
      }
    }

    return {
      windowIdsByWorkspace,
      getWindow: (id: string) => store.getState().windows.find((w) => w.id === id),
      getWindowsSnapshot: () => store.getState().windows,
      workspaces,
      activeWorkspaceId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowListKey, workspaces, activeWorkspaceId, store]);

  const windowVersionValue = useMemo<OsWindowVersionContextType>(
    () => ({
      getWindow: (id: string) => store.getState().windows.find((w) => w.id === id),
      subscribeToWindow: (windowId: string, callback: () => void) => {
        let prev = store.getState().windows.find((w) => w.id === windowId);
        return store.subscribe(() => {
          const next = store.getState().windows.find((w) => w.id === windowId);
          if (next !== prev) {
            prev = next;
            callback();
          }
        });
      },
    }),
    [store],
  );

  const windowParamsKey = windows.map((w) => `${w.id}:${JSON.stringify(w.params)}`).join(",");

  const focusValue = useMemo<OsFocusContextType>(() => {
    const visible = visibleWindowsInWorkspace(windows, activeWorkspaceId);
    const hasMaximizedWindowInActiveWorkspace = visible.some((w) => w.isMaximized);
    const focused = topVisibleWindowInWorkspace(windows, activeWorkspaceId);
    if (!focused) {
      return {
        focusedWindowId: null,
        focusedWindowKey: null,
        getFocusedWindow: () => undefined,
        hasMaximizedWindowInActiveWorkspace: false,
      };
    }
    return {
      focusedWindowId: focused.id,
      focusedWindowKey: `${focused.id}:${JSON.stringify(focused.params)}`,
      getFocusedWindow: () => topVisibleWindowInWorkspace(windows, activeWorkspaceId),
      hasMaximizedWindowInActiveWorkspace,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOrderKey, windowParamsKey, activeWorkspaceId]);

  const dockTooltipsRef = useRef<Map<string, string>>(new Map());
  const [, forceTooltipUpdate] = useReducer((x: number) => x + 1, 0);

  const dockTooltipsValue = useMemo<OsDockTooltipsContextType>(
    () => ({
      getTooltip: (appId: string) => dockTooltipsRef.current.get(appId) ?? null,
      setTooltip: (appId: string, tooltip: string | null) => {
        if (tooltip === null) {
          dockTooltipsRef.current.delete(appId);
        } else {
          dockTooltipsRef.current.set(appId, tooltip);
        }
        forceTooltipUpdate();
      },
      clearAll: () => {
        dockTooltipsRef.current.clear();
        forceTooltipUpdate();
      },
    }),
    [],
  );

  return (
    <OsContext.Provider value={true}>
      <OsSettingsContext.Provider value={settingsValue}>
        <OsActionsContext.Provider value={actionsValue}>
          <OsFocusContext.Provider value={focusValue}>
            <OsDockTooltipsContext.Provider value={dockTooltipsValue}>
              <OsOpenAppsContext.Provider value={openAppsValue}>
                <OsWindowVersionContext.Provider value={windowVersionValue}>
                  <OsWindowListContext.Provider value={windowListValue}>
                    <OsWindowsContext.Provider value={windowsValue}>
                      {children}
                    </OsWindowsContext.Provider>
                  </OsWindowListContext.Provider>
                </OsWindowVersionContext.Provider>
              </OsOpenAppsContext.Provider>
            </OsDockTooltipsContext.Provider>
          </OsFocusContext.Provider>
        </OsActionsContext.Provider>
      </OsSettingsContext.Provider>
    </OsContext.Provider>
  );
}
